import { useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/db/client";

/**
 * One department a person is limited to (#7470).
 *
 * A person with NO rows is unrestricted and sees every department — absence
 * means unrestricted, not empty. So removing somebody's last department widens
 * them to everything; both screens that can do that warn first. The rule
 * itself lives in the database (in_my_departments), not here.
 */
export interface DepartmentGrant {
  id: string;
  user_id: string;
  subsheet_id: string;
}

/**
 * Every grant the caller may read, flat: all of them for an admin, only their
 * own for anyone else (RLS decides). One query for the same reason
 * useTeamMemberships is one — the table is small and several screens slice it.
 */
const useDepartmentAccess = () =>
  useQuery<DepartmentGrant[]>({
    queryKey: ["department-access"],
    queryFn: async () => {
      const { data, error } = await supabase.from("user_subsheet_access").select("id, user_id, subsheet_id");
      if (error) throw error;
      return (data ?? []) as DepartmentGrant[];
    },
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });

/** The departments one person is limited to. Empty means unrestricted. */
export function useDepartmentsForPerson(userId: string | null) {
  const { data = [], ...rest } = useDepartmentAccess();
  const grants = useMemo(() => (userId ? data.filter((g) => g.user_id === userId) : []), [data, userId]);
  return { ...rest, grants };
}

/**
 * The people for whom `subsheetId` is their ONLY department — deleting it
 * leaves them unrestricted. The delete dialog warns about exactly these.
 */
export function usePeopleWidenedByDeleting(subsheetId: string | null) {
  const { data = [], ...rest } = useDepartmentAccess();
  const userIds = useMemo(() => {
    if (!subsheetId) return [];
    const counts = new Map<string, number>();
    for (const g of data) counts.set(g.user_id, (counts.get(g.user_id) ?? 0) + 1);
    return data.filter((g) => g.subsheet_id === subsheetId && counts.get(g.user_id) === 1).map((g) => g.user_id);
  }, [data, subsheetId]);
  return { ...rest, userIds };
}

/**
 * Re-read everything when anybody's department access changes.
 *
 * Everything, not a list of keys: a change can widen or narrow what the viewer
 * reads on every screen — tickets, counts, activity, navigation — and the
 * database already enforces the new rule on the next query. This is only the
 * open screen catching up. Access changes are rare admin actions, so the
 * blunt instrument costs nothing that matters.
 */
export const useGlobalDepartmentAccessSync = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("global-department-access-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "user_subsheet_access" }, () => {
        queryClient.invalidateQueries();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
};

export const useGrantDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, subsheetId }: { userId: string; subsheetId: string }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error } = await supabase.from("user_subsheet_access").insert({
        user_id: userId,
        subsheet_id: subsheetId,
        created_by_id: user?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["department-access"] });
      toast.success("Department access added.");
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(/duplicate|unique/i.test(message) ? "They already have that department." : message);
    },
  });
};

export const useRevokeDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ grantId }: { grantId: string }) => {
      const { error } = await supabase.from("user_subsheet_access").delete().eq("id", grantId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["department-access"] });
      toast.success("Department access removed.");
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : String(error));
    },
  });
};
